import classnames from "classnames";
import pluralize from "pluralize";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";

import ActionButton from "../../shared/components/ActionButton";
import ScrollMonitor from "../../shared/components/ScrollMonitor";
import { policies, resources, services, users } from "../actions";
import { PolicyDetailsShape, PolicyShape } from "../proptypes";
import selectors from "../selectors";
import PoliciesSummaryItem from "./PoliciesSummaryItem";

class PoliciesSummary extends Component {
  state = {
    showSummary: false,
    listScrolled: false,
    message: "",
    numChanges: 0
  };

  static getDerivedStateFromProps(props, state) {
    const numChanges =
      props.policiesForUpdate.length + props.policiesForDelete.length;
    if (numChanges !== state.numChanges) {
      if (numChanges === 0) {
        return {
          numChanges,
          showSummary: false
        };
      }
      return {
        numChanges
      };
    }
    return null;
  }

  handleChange = event => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  onScroll = event => {
    const { target } = event;
    const { listScrolled } = this.state;
    if (!listScrolled && target.scrollTop > 0) {
      this.setState({ listScrolled: true });
    } else if (listScrolled && target.scrollTop === 0) {
      this.setState({ listScrolled: false });
    }
  };

  toggleDisplay = () => {
    const { showSummary } = this.state;
    this.setState({ showSummary: !showSummary });
  };

  editPolicy = (policy, label) => {
    const {
      setCurrentService,
      setCurrentResource,
      setEntityFilterTerm
    } = this.props;

    setEntityFilterTerm(label.toLowerCase());
    setCurrentResource(policy.resource.$uri);
    setCurrentService(policy.service.$uri);
  };

  buttonText = () => {
    const { hasDirtyPolicies, saving } = this.props;
    const { numChanges } = this.state;
    if (saving) {
      return "Saving...";
    }
    if (hasDirtyPolicies) {
      return `Save ${numChanges} ${pluralize("change", numChanges)}`;
    }
    return "Save";
  };

  render() {
    const {
      commit,
      policiesForUpdate,
      policiesForUpdateDetails,
      policiesForDelete,
      policiesForDeleteDetails,
      hasDirtyPolicies,
      removePolicy,
      markClean,
      saving,
      loading
    } = this.props;
    const { listScrolled, message, showSummary } = this.state;

    return (
      <div className="l-policies__summary" style={{ gridArea: "footer" }}>
        {showSummary && (
          <div className="l-policies__summary-body">
            <ScrollMonitor
              className={classnames("p-table--muted", {
                "p-table--muted-scrolled": listScrolled
              })}
              onScroll={this.onScroll}
              style={{ maxHeight: "35vh" }}
            >
              {policiesForUpdate.length > 0 && (
                <table className="p-table--summary u-no-margin--bottom">
                  <thead className="p-sticky-table-header">
                    <tr>
                      <th scope="col" colSpan="3">
                        Groups/Users
                      </th>
                      <th scope="col" colSpan="3">
                        Service
                      </th>
                      <th scope="col" colSpan="4">
                        Scope
                      </th>
                      <th scope="col" colSpan="5">
                        Roles
                      </th>
                      <th scope="col" colSpan="2" />
                    </tr>
                  </thead>
                  <tbody>
                    {policiesForUpdateDetails.map(policy => (
                      <PoliciesSummaryItem
                        key={policy.id}
                        policy={policy}
                        editPolicy={this.editPolicy}
                        removePolicy={removePolicy}
                      />
                    ))}
                  </tbody>
                </table>
              )}

              {policiesForDelete.length > 0 && (
                <table className="p-table--summary u-no-margin--bottom">
                  <thead className="p-sticky-table-header">
                    <tr>
                      <th scope="col" colSpan="13">
                        Policies to remove
                      </th>
                      <th scope="col" colSpan="1" />
                      <th scope="col" colSpan="1" />
                      <th scope="col" colSpan="1" />
                      <th scope="col" colSpan="1" />
                    </tr>
                  </thead>
                  <tbody>
                    {policiesForDeleteDetails.map(policy => (
                      <PoliciesSummaryItem
                        key={policy.$uri}
                        policy={policy}
                        editPolicy={this.editPolicy}
                        removePolicy={markClean}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </ScrollMonitor>
            {policiesForUpdate.length > 0 && (
              <div className="l-policies__summary-description">
                <div className="l-policies__summary-description-header p-table--muted-header p-muted-heading u-no-margin--bottom">
                  Description
                </div>
                <div className="l-policies__summary-description-input">
                  <textarea
                    maxLength="120"
                    name="message"
                    onChange={this.handleChange}
                    placeholder="Add a description"
                    value={message}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="l-flex-end-centered">
          <button
            type="button"
            className={classnames("p-button--base", "u-no-margin--bottom", {
              "u-hide": !hasDirtyPolicies
            })}
            onClick={this.toggleDisplay}
            onKeyPress={this.toggleDisplay}
          >
            <i
              className={classnames("p-icon--chevron", {
                "u-mirror--y": !showSummary
              })}
            />
            <span className="p-icon__text">
              {`${showSummary ? "Hide" : "See"} changes`}
            </span>
          </button>
          <ActionButton
            type="positive"
            inProgress={saving}
            disabled={saving || loading || !hasDirtyPolicies}
            onClick={() => {
              commit([...policiesForUpdate, ...policiesForDelete], message);
            }}
          >
            {this.buttonText()}
          </ActionButton>
        </div>
      </div>
    );
  }
}

PoliciesSummary.propTypes = {
  policiesForUpdate: PropTypes.arrayOf(PolicyShape).isRequired,
  policiesForUpdateDetails: PropTypes.arrayOf(PolicyDetailsShape).isRequired,
  policiesForDelete: PropTypes.arrayOf(PolicyShape).isRequired,
  policiesForDeleteDetails: PropTypes.arrayOf(PolicyDetailsShape).isRequired,
  hasDirtyPolicies: PropTypes.bool.isRequired,
  commit: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  removePolicy: PropTypes.func.isRequired,
  markClean: PropTypes.func.isRequired,
  setCurrentResource: PropTypes.func.isRequired,
  setCurrentService: PropTypes.func.isRequired,
  setEntityFilterTerm: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  policiesForUpdate: selectors.policies.getForUpdate(state),
  policiesForUpdateDetails: selectors.policies.getForUpdateDetails(state),
  policiesForDelete: selectors.policies.getForDelete(state),
  policiesForDeleteDetails: selectors.policies.getForDeleteDetails(state),
  hasDirtyPolicies: selectors.policies.hasDirty(state),
  saving: state.policies.saving,
  loading: state.policies.loading
});

const mapDispatchToProps = {
  commit: policies.commit,
  markClean: policies.markClean,
  setCurrentResource: resources.setCurrent,
  setCurrentService: services.setCurrent,
  setEntityFilterTerm: users.setFilterTerm,
  removePolicy: policies.removeDirty
};

export { PoliciesSummary as PoliciesSummaryComponent };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PoliciesSummary);
